export type TideScaleRefusalReason = "TIDE_SCALE_UNBOUNDED" | "TIDE_SCALE_ZERO_WIDTH";

export interface TideScaleOk {
  ok: true;
  x(tMs: number): number;
  tAt(x: number): number;
}

export interface TideScaleRefusal {
  ok: false;
  reason: TideScaleRefusalReason;
}

export type TideScale = TideScaleOk | TideScaleRefusal;
export type TideScaleDisposition = "CHIP" | "LOUD";

export const TIDE_SCALE_CHIP: Readonly<Record<TideScaleRefusalReason, string>> = {
  TIDE_SCALE_ZERO_WIDTH: "TIDE UNMEASURED — width 0, no draw",
  TIDE_SCALE_UNBOUNDED: "TIDE UNBOUNDED — no session bounds, no draw",
};

/** Pure elapsed-time scale. Invalid geometry is data, never an exception. */
export function tideScale(
  startMs: number,
  endMs: number,
  left: number,
  plotWidth: number,
): TideScale {
  if (![startMs, endMs, left].every(Number.isFinite) || endMs < startMs) {
    return { ok: false, reason: "TIDE_SCALE_UNBOUNDED" };
  }
  if (!Number.isFinite(plotWidth) || plotWidth <= 0) {
    return { ok: false, reason: "TIDE_SCALE_ZERO_WIDTH" };
  }
  if (endMs === startMs) {
    return {
      ok: true,
      x: () => left,
      tAt: () => startMs,
    };
  }
  return {
    ok: true,
    x(tMs) {
      if (!Number.isFinite(tMs)) return left;
      const clamped = Math.max(startMs, Math.min(endMs, tMs));
      return left + ((clamped - startMs) / (endMs - startMs)) * plotWidth;
    },
    tAt(x) {
      if (!Number.isFinite(x)) return startMs;
      const clampedX = Math.max(left, Math.min(left + plotWidth, x));
      return startMs + ((clampedX - left) / plotWidth) * (endMs - startMs);
    },
  };
}

interface TideScaleVenueState {
  verdict: string | null;
}

let tideScaleVenueState: TideScaleVenueState | null = null;
const reportedRefusals = new Set<string>();

export function noteTideScaleVenueState(state: TideScaleVenueState | null): void {
  tideScaleVenueState = state;
}

export function reportTideScaleRefusal(
  refusal: TideScaleRefusal,
  site: string,
): TideScaleDisposition {
  if (tideScaleVenueState?.verdict === "CLOSED") return "CHIP";
  const key = `${refusal.reason}\u0000${site}`;
  if (!reportedRefusals.has(key)) {
    reportedRefusals.add(key);
    console.error(`[tideScale] ${refusal.reason} · ${site}`);
  }
  return "LOUD";
}

export function resetTideScaleRefusals(): void {
  reportedRefusals.clear();
}
